import React, { useState, useEffect } from "react";
import { useCompany } from "../../../contexts/CompanyContext";
import { bankAccountsApi } from "../../../services";
import type { BankAccountAttributes } from "../../../types";
import type { ImportResult } from "../../../types";
import {
  ConfirmationModal,
  Card,
  EmptyState,
  Loading,
  ErrorState,
  ImportResultCard,
  ExcelImportButton,
  IconsLarge,
} from "../../shared";
import { AdminPageHeader } from "../shared";
import { CreateBankAccountModal } from "./CreateBankAccountModal";
import { BankAccountsListView } from "./BankAccountsListView";

export const Contas: React.FC = () => {
  const { company } = useCompany();
  const companyId = company?.id ?? 0;
  const [accounts, setAccounts] = useState<BankAccountAttributes[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccountAttributes | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const fetchAccounts = async () => {
    if (companyId === 0) {
      setAccounts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await bankAccountsApi.getAll(companyId);
      if (response.success && response.data) {
        setAccounts(response.data);
      } else {
        setError(response.error ?? "Falha ao carregar contas");
      }
    } catch {
      setError("Falha ao carregar contas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [companyId]);

  const handleEdit = (account: BankAccountAttributes) => {
    setEditingAccount(account);
    setShowCreateForm(true);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingAccount(null);
  };

  const handleSaved = () => {
    fetchAccounts();
    setShowCreateForm(false);
    setEditingAccount(null);
  };

  const handleDelete = (id: number) => {
    setAccountToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!accountToDelete) return;
    setDeleteLoading(true);
    setError(null);
    try {
      const response = await bankAccountsApi.delete(accountToDelete);
      if (response.success) {
        await fetchAccounts();
        setShowDeleteModal(false);
        setAccountToDelete(null);
      } else {
        setError(response.error ?? "Falha ao excluir conta");
      }
    } catch {
      setError("Falha ao excluir conta");
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setAccountToDelete(null);
  };

  const handleImportFile = async (file: File) => {
    if (companyId === 0) return;
    setImportLoading(true);
    setError(null);
    setImportResult(null);
    try {
      const response = await bankAccountsApi.importFromExcel(companyId, file);
      if (response.success && response.data) {
        setImportResult(response.data);
        if ((response.data.entities?.bankAccounts?.created ?? 0) > 0) {
          await fetchAccounts();
        }
      } else {
        setError(response.error ?? "Falha ao importar planilha.");
      }
    } catch {
      setError("Falha ao importar planilha.");
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<IconsLarge.BankAccount />}
        title="Portadores"
        subtitle="Gerencie os portadores da empresa"
        buttonText="Novo portador"
        onButtonClick={() => {
          setEditingAccount(null);
          setShowCreateForm(true);
        }}
        buttonDisabled={loading}
        showViewToggle={false}
        rightContent={
          <ExcelImportButton
            onFileSelect={handleImportFile}
            disabled={loading || companyId === 0}
            loading={importLoading}
          />
        }
      />

      {error && (
        <ErrorState
          message={error}
          variant="card"
          onRetry={() => fetchAccounts()}
        />
      )}

      {importResult && (
        <ImportResultCard
          result={importResult}
          onClose={() => setImportResult(null)}
          title="Importação de contas concluída"
        />
      )}

      <CreateBankAccountModal
        isOpen={showCreateForm}
        onClose={handleCancel}
        onSuccess={handleSaved}
        companyId={companyId}
        editingAccount={editingAccount}
      />

      {loading && !showCreateForm ? (
        <Loading
          message="Carregando contas..."
          size="xl"
        />
      ) : (
        <Card className="p-8">
          {accounts.length === 0 ? (
            <EmptyState
              icon={<IconsLarge.BankAccount />}
              title="Nenhuma conta encontrada"
              description="Cadastre a primeira conta para começar."
              actionText="Nova conta"
              onAction={() => setShowCreateForm(true)}
            />
          ) : (
            <BankAccountsListView
              accounts={accounts}
              onEdit={handleEdit}
              onDelete={handleDelete}
              loading={loading}
            />
          )}
        </Card>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir conta"
        message="Tem certeza que deseja excluir esta conta? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default Contas;
